/**
 * ObjectiveEditor — where the user "chooses the metrics" (UI-SPEC §3.2). One row
 * per dimension (correctness/tokens/latency/cost): weight + the relevant
 * floor/ceiling/minimize control, with a live plain-language restatement.
 * Objective changes are HIGH-IMPACT, so onSave routes through the human gate
 * (the caller shows "pending sign-off"); the editor itself just emits the JSON.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { Input } from "../../input";
import { Button } from "../../button";

interface Dim {
  weight?: number;
  floor?: number;
  ceiling?: number;
  minimize?: boolean;
}
type Objective = Record<string, Dim>;

const DEFAULT: Objective = {
  correctness: { weight: 1, floor: 1.0 },
  tokens: { weight: 0.3, minimize: true },
  latency_ms: { weight: 0.2, ceiling: 600000 },
  cost_usd: { weight: 0.5, minimize: true },
};

function restate(o: Objective): string {
  const parts: string[] = [];
  if (o.correctness?.floor != null) parts.push(`never drop below ${Math.round(o.correctness.floor * 100)}% of today's correctness`);
  if (o.latency_ms?.ceiling != null) parts.push(`keep latency under ${(o.latency_ms.ceiling / 1000).toFixed(0)}s`);
  const mins = ["tokens", "cost_usd"].filter((k) => o[k]?.minimize).map((k) => (k === "cost_usd" ? "cost" : k));
  if (mins.length) parts.push(`then minimize ${mins.join(" + ")}`);
  return parts.length ? parts.join("; ") + "." : "No constraints set.";
}

export function ObjectiveEditor({
  initial,
  onSave,
  saveLabel = "Save objective (enters sign-off)",
}: {
  initial?: string; // JSON
  onSave?: (json: string) => void | Promise<void>;
  saveLabel?: string;
}) {
  const [obj, setObj] = React.useState<Objective>(() => {
    try {
      return initial ? { ...DEFAULT, ...(JSON.parse(initial) as Objective) } : DEFAULT;
    } catch {
      return DEFAULT;
    }
  });
  const [pending, setPending] = React.useState(false);

  const setDim = (dim: string, patch: Partial<Dim>) =>
    setObj((o) => ({ ...o, [dim]: { ...o[dim], ...patch } }));

  const rows: Array<{ key: string; label: string; control: "floor" | "ceiling" | "minimize" }> = [
    { key: "correctness", label: "Correctness", control: "floor" },
    { key: "tokens", label: "Tokens", control: "minimize" },
    { key: "latency_ms", label: "Latency (ms)", control: "ceiling" },
    { key: "cost_usd", label: "Cost ($)", control: "minimize" },
  ];

  return (
    <Card>
      <CardHeader><CardTitle>Objective</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-wrap items-center gap-3 type-body">
            <span className="w-28 text-foreground">{r.label}</span>
            <label className="flex items-center gap-1 text-muted-foreground">
              weight
              <Input
                type="number" step="0.1" className="w-20"
                value={obj[r.key]?.weight ?? 0}
                onChange={(e) => setDim(r.key, { weight: Number(e.target.value) })}
              />
            </label>
            {r.control === "floor" && (
              <label className="flex items-center gap-1 text-muted-foreground">
                floor (×incumbent)
                <Input type="number" step="0.01" className="w-20"
                  value={obj[r.key]?.floor ?? 1}
                  onChange={(e) => setDim(r.key, { floor: Number(e.target.value) })} />
              </label>
            )}
            {r.control === "ceiling" && (
              <label className="flex items-center gap-1 text-muted-foreground">
                ceiling
                <Input type="number" className="w-28"
                  value={obj[r.key]?.ceiling ?? 0}
                  onChange={(e) => setDim(r.key, { ceiling: Number(e.target.value) })} />
              </label>
            )}
            {r.control === "minimize" && (
              <label className="flex items-center gap-1 text-muted-foreground">
                <input type="checkbox" className="size-4"
                  checked={obj[r.key]?.minimize ?? false}
                  onChange={(e) => setDim(r.key, { minimize: e.target.checked })} />
                minimize
              </label>
            )}
          </div>
        ))}
        <p className="type-subtext text-muted-foreground">“{restate(obj)}”</p>
        {onSave && (
          <div>
            <Button
              size="sm"
              disabled={pending}
              onClick={async () => { setPending(true); try { await onSave(JSON.stringify(obj)); } finally { setPending(false); } }}
            >
              {pending ? "Saving…" : saveLabel}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
